import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  FileText, UserPlus, UserMinus, Key, RefreshCw, 
  ChevronLeft, ChevronRight, Clock, User, Filter
} from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

const ACTION_ICONS = {
  agent_created: UserPlus,
  agent_deleted: UserMinus,
  password_reset_initiated: Key,
  password_changed: Key,
  role_changed: Key,
  default: FileText
};

const ACTION_COLORS = {
  agent_created: "bg-green-100 text-green-800 border-green-300",
  agent_deleted: "bg-red-100 text-red-800 border-red-300",
  password_reset_initiated: "bg-amber-100 text-amber-800 border-amber-300",
  password_changed: "bg-blue-100 text-blue-800 border-blue-300",
  role_changed: "bg-purple-100 text-purple-800 border-purple-300",
  default: "bg-gray-100 text-gray-800 border-gray-300"
};

export default function AgentAuditLogs() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [page, actionFilter]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString()
      });
      if (actionFilter) params.append("action", actionFilter);

      const [logsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/agent/audit-logs?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/audit-logs/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setLogs(logsRes.data.logs);
      setTotal(logsRes.data.total);
      setSummary(summaryRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view audit logs");
      } else {
        toast.error("Failed to load audit logs");
      }
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || ACTION_ICONS.default;
    return <Icon className="h-4 w-4" />;
  };

  const getActionColor = (action) => {
    return ACTION_COLORS[action] || ACTION_COLORS.default;
  };

  const formatAction = (action) => {
    return action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && logs.length === 0) {
    return (
      <AgentLayout title="Audit Logs">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Audit Logs">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          Audit Logs
        </h1>
        <p className="text-muted-foreground">Track all team management activities</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-foreground">{summary.total_logs}</p>
              <p className="text-sm text-muted-foreground">Total Logs</p>
            </CardContent>
          </Card>
          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-blue-600">{summary.recent_24h}</p>
              <p className="text-sm text-muted-foreground">Last 24 Hours</p>
            </CardContent>
          </Card>
          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-green-600">{summary.by_action?.agent_created || 0}</p>
              <p className="text-sm text-muted-foreground">Members Created</p>
            </CardContent>
          </Card>
          <Card className="dashboard-card">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-amber-600">{summary.by_action?.password_reset_initiated || 0}</p>
              <p className="text-sm text-muted-foreground">Password Resets</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={actionFilter || "all"} onValueChange={(val) => setActionFilter(val === "all" ? "" : val)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="agent_created">Agent Created</SelectItem>
              <SelectItem value="agent_deleted">Agent Deleted</SelectItem>
              <SelectItem value="password_reset_initiated">Password Reset</SelectItem>
              <SelectItem value="password_changed">Password Changed</SelectItem>
              <SelectItem value="role_changed">Role Changed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Logs List */}
      <Card className="card-organic">
        <CardContent className="p-0">
          {logs.length > 0 ? (
            <div className="divide-y divide-border">
              {logs.map((log, index) => (
                <div key={index} className="p-4 hover:bg-muted/50">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${getActionColor(log.action).split(' ')[0]}`}>
                      {getActionIcon(log.action)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${getActionColor(log.action)} border`}>
                          {formatAction(log.action)}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm space-y-1">
                        {log.agent_info && (
                          <p>
                            <span className="text-muted-foreground">Target: </span>
                            <span className="font-medium">{log.agent_info.full_name || log.agent_info.email}</span>
                          </p>
                        )}
                        {log.created_by_info && (
                          <p>
                            <span className="text-muted-foreground">By: </span>
                            <span className="font-medium">{log.created_by_info.full_name || log.created_by_info.email}</span>
                          </p>
                        )}
                        {log.deleted_by_info && (
                          <p>
                            <span className="text-muted-foreground">Deleted by: </span>
                            <span className="font-medium">{log.deleted_by_info.full_name || log.deleted_by_info.email}</span>
                          </p>
                        )}
                        {log.role_assigned && (
                          <p>
                            <span className="text-muted-foreground">Role: </span>
                            <span className="font-medium capitalize">{log.role_assigned?.replace('_', ' ')}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No audit logs found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </AgentLayout>
  );
}
